import React, {useCallback, useEffect, useState, useRef} from 'react';
import './styles.css';
import {cn} from '@/lib/utils';

type Props = {
  min: number;
  max: number;
  currentMin?: number;
  currentMax?: number;
  onChange: ({min, max}: {min: number; max: number}) => void;
  reset?: boolean;
  setReset?: (reset: boolean) => void;
};

const MultiRangeSlider = ({
  min,
  max,
  currentMin,
  currentMax,
  onChange,
  reset,
  setReset,
}: Props) => {
  const [minVal, setMinVal] = useState(currentMin ? currentMin : min);
  const [maxVal, setMaxVal] = useState(currentMax ? currentMax : max);
  const minValRef = useRef<HTMLInputElement | null>(null);
  const maxValRef = useRef<HTMLInputElement | null>(null);
  const range = useRef<HTMLDivElement | null>(null);

  const getPercent = useCallback(
    (value: number) => Math.round(((value - min) / (max - min)) * 100),
    [min, max],
  );

  useEffect(() => {
    if (reset && setReset) {
      setMinVal(min);
      setMaxVal(max);
      setReset(false);
    }
    // eslint-disable-next-line
  }, [reset]);

  useEffect(() => {
    if (maxValRef.current) {
      const minPercent = getPercent(minVal);
      const maxPercent = getPercent(+maxValRef.current.value);

      if (range.current) {
        range.current.style.left = `${minPercent}%`;
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [minVal, getPercent]);

  useEffect(() => {
    if (minValRef.current) {
      const minPercent = getPercent(+minValRef.current.value);
      const maxPercent = getPercent(maxVal);

      if (range.current) {
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [maxVal, getPercent]);

  useEffect(() => {
    onChange({min: minVal, max: maxVal});
  }, [minVal, maxVal, onChange]);

  return (
    <div className="relative w-full flex items-center justify-center my-4">
      <input
        type="range"
        min={min}
        max={max}
        value={minVal}
        ref={minValRef}
        onChange={(event) => {
          const value = Math.min(+event.target.value, maxVal - 1);
          setMinVal(value);
          event.target.value = value.toString();
        }}
        className={cn('thumb thumb--zindex-3 outline-none', {
          'thumb--zindex-5': minVal > max - 100,
        })}
      />
      <input
        type="range"
        min={min}
        max={max}
        value={maxVal}
        ref={maxValRef}
        onChange={(event) => {
          const value = Math.max(+event.target.value, minVal + 1);
          setMaxVal(value);
          event.target.value = value.toString();
        }}
        className="thumb thumb--zindex-4 outline-none"
      />

      <div className="slider">
        <div className="slider__track" />
        <div ref={range} className="slider__range" />
      </div>
    </div>
  );
};

export default MultiRangeSlider;
